import { useContext } from 'react';

import { TrackingContext } from '@/providers/TrackingProvider';

/**
 * Hook to access the new trackEvent function from TrackingContext.
 * This is the recommended way to track events going forward.
 *
 * The new API uses a simplified parameter signature with an object containing:
 * - type: The event action type
 * - detail: Event data and information
 * - category: Optional event category
 *
 * @returns trackEvent function with app context automatically applied
 *
 * @example
 * ```tsx
 * import { useTrackEvent } from '@/hooks/tracking/useTrackEvent';
 *
 * function MyComponent() {
 *     const trackEvent = useTrackEvent();
 *
 *     const handleClick = () => {
 *         trackEvent({
 *             type: 'click',
 *             detail: { button: 'submit', page: 'checkout' },
 *             category: 'user_action'
 *         });
 *     };
 *
 *     return <button onClick={handleClick}>Click me</button>;
 * }
 * ```
 */
const useTrackEvent = () => {
    const context = useContext(TrackingContext);

    if (!context) {
        throw new Error('useTrackEvent must be used within a TrackingProvider');
    }

    return context.trackEvent;
};

export default useTrackEvent;
